import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import './NotificationSystem.css';

interface Notification {
  id: string;
  type: 'token-deployment' | 'sentry-bought' | 'copy-trader-alert' | 'pwa-swap';
  title: string;
  message: string;
  timestamp: Date;
  data?: any;
}

interface NotificationSystemProps {
  apiUrl: string;
  pollInterval?: number; // milliseconds (default: 10000 = 10 seconds)
  maxNotifications?: number; // max visible at once (default: 5)
  autoDismissTime?: number; // milliseconds (default: 5000 = 5 seconds)
  placement?: 'default' | 'pwaHeader';
  enablePwaSwaps?: boolean;
}

export const NotificationSystem: React.FC<NotificationSystemProps> = ({
  apiUrl,
  pollInterval = 10000,
  maxNotifications = 5,
  autoDismissTime = 5000,
  placement = 'default',
  enablePwaSwaps = false
}) => {
  const [currentNotification, setCurrentNotification] = useState<Notification | null>(null);
  const [lastDeploymentTimestamp, setLastDeploymentTimestamp] = useState<string | null>(null);
  const [lastSentryTotal, setLastSentryTotal] = useState<number | null>(null);
  const [lastAlertTimestamp, setLastAlertTimestamp] = useState<string | null>(null);
  const [lastPwaSwapId, setLastPwaSwapId] = useState<number | null>(null);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  // Fetch token deployments
  const fetchDeployments = useCallback(async () => {
    if (!apiUrl) return;
    
    try {
      const response = await fetch(`${apiUrl}/api/token-deployments?limit=5`);
      const data = await response.json();
      
      if (data.deployments && data.deployments.length > 0) {
        const latest = data.deployments[0];
        
        // On initial load, just set the timestamp without showing notification
        if (!isInitialized) {
          setLastDeploymentTimestamp(latest.timestamp);
          return;
        }
        
        // Only show if this is a new deployment (after initialization)
        if (latest.timestamp !== lastDeploymentTimestamp) {
          setLastDeploymentTimestamp(latest.timestamp);
          
          const notification: Notification = {
            id: `deployment-${latest.tokenMintAddress}-${Date.now()}`,
            type: 'token-deployment',
            title: '🚀 New Token Deployed',
            message: `${latest.tokenName || latest.tokenSymbol} (${latest.tokenSymbol}) has been deployed!`,
            timestamp: new Date(),
            data: latest
          };
          
          addNotification(notification);
        }
      }
    } catch (error) {
      console.error('Error fetching deployments:', error);
    }
  }, [apiUrl, lastDeploymentTimestamp, isInitialized]);

  // Fetch total SENTRY bought
  const fetchSentryTotal = useCallback(async () => {
    if (!apiUrl) return;
    
    try {
      const response = await fetch(`${apiUrl}/api/sentry-total-bought`);
      const data = await response.json();
      
      if (data.totalSentryBought !== null) {
        // On initial load, just set the total without showing notification
        if (!isInitialized) {
          setLastSentryTotal(data.totalSentryBought);
          return;
        }
        
        // Only show if there's a change after initialization
        if (data.totalSentryBought !== lastSentryTotal) {
          const previous = lastSentryTotal || 0;
          const current = data.totalSentryBought;
          const difference = current - previous;
          
          setLastSentryTotal(current);
          
          // Only show notification if there's a significant increase (> 0.01)
          if (difference > 0.01) {
            const notification: Notification = {
              id: `sentry-${Date.now()}`,
              type: 'sentry-bought',
              title: '💰 SENTRY Tokens Bought',
              message: `+${difference.toFixed(2)} $SENTRY tokens forwarded to index fund (Total: ${current.toFixed(2)})`,
              timestamp: new Date(),
              data: { total: current, difference }
            };
            
            addNotification(notification);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching SENTRY total:', error);
    }
  }, [apiUrl, lastSentryTotal, isInitialized]);

  // Fetch copy trader alerts
  const fetchCopyTraderAlerts = useCallback(async () => {
    if (!apiUrl) return;
    
    try {
      const response = await fetch(`${apiUrl}/api/copy-trader-alerts`);
      const data = await response.json();
      
      if (data.alerts && data.alerts.length > 0) {
        // Find the most recent alert we haven't seen
        const latest = data.alerts[0];
        
        // On initial load, just set the timestamp without showing notification
        if (!isInitialized) {
          setLastAlertTimestamp(latest.createdAt);
          return;
        }
        
        // Only show if this is a new alert (after initialization)
        if (latest.createdAt !== lastAlertTimestamp) {
          setLastAlertTimestamp(latest.createdAt);
          
          const notification: Notification = {
            id: `alert-${latest.chatId}-${latest.copiedTraderId}-${Date.now()}`,
            type: 'copy-trader-alert',
            title: '📋 New Copy Trade Subscription',
            message: `${latest.groupName} is now copying trades from ${latest.traderUsername || `Trader ${latest.copiedTraderId}`}`,
            timestamp: new Date(),
            data: latest
          };
          
          addNotification(notification);
        }
      }
    } catch (error) {
      console.error('Error fetching copy trader alerts:', error);
    }
  }, [apiUrl, lastAlertTimestamp, isInitialized]);

  // Remove notification
  const removeNotification = useCallback(() => {
    setCurrentNotification(null);
  }, []);

  // Add notification (replaces current one)
  const addNotification = useCallback((notification: Notification) => {
    // Replace current notification
    setCurrentNotification(notification);

    // Auto-dismiss after specified time
    setTimeout(() => {
      removeNotification();
    }, autoDismissTime);
  }, [autoDismissTime, removeNotification]);

  const fetchPwaSwaps = useCallback(async () => {
    if (!apiUrl || !enablePwaSwaps) return;

    try {
      const response = await fetch(`${apiUrl}/api/pwa/swaps?limit=1`);
      if (!response.ok) {
        return;
      }
      const data = await response.json();
      if (!data?.data || data.data.length === 0) return;

      const latest = data.data[0];
      const latestId = Number(latest.id);
      if (!Number.isFinite(latestId)) return;

      if (!isInitialized) {
        setLastPwaSwapId(latestId);
        return;
      }

      if (latestId !== lastPwaSwapId) {
        setLastPwaSwapId(latestId);
        const direction = String(latest.direction || '').toUpperCase();
        const tokenSymbol = latest.tokenSymbol || 'TOKEN';
        const tokenAmount = latest.tokenAmount ? Number(latest.tokenAmount) : null;
        const solAmount = latest.solAmount ? Number(latest.solAmount) : null;
        const amountText = tokenAmount
          ? `${tokenAmount.toLocaleString(undefined, { maximumFractionDigits: 6 })} ${tokenSymbol}`
          : tokenSymbol;
        const solText = solAmount ? ` for ${solAmount.toFixed(4)} SOL` : '';

        const notification: Notification = {
          id: `pwa-swap-${latestId}-${Date.now()}`,
          type: 'pwa-swap',
          title: '⚡ PWA Trade',
          message: `${direction} ${amountText}${solText}`,
          timestamp: new Date(),
          data: latest
        };

        addNotification(notification);
      }
    } catch (error) {
      console.error('Error fetching PWA swaps:', error);
    }
  }, [apiUrl, enablePwaSwaps, isInitialized, lastPwaSwapId, addNotification]);

  // Poll APIs at intervals
  useEffect(() => {
    if (!apiUrl) return;
    
    // Initial fetch - just to set baseline, don't show notifications
    const initialize = async () => {
      const jobs = [fetchDeployments(), fetchSentryTotal(), fetchCopyTraderAlerts()];
      if (enablePwaSwaps) {
        jobs.push(fetchPwaSwaps());
      }
      await Promise.all(jobs);
      // Mark as initialized after initial fetch completes
      setIsInitialized(true);
    };
    
    initialize();

    // Set up polling (only after initialization)
    const deploymentInterval = setInterval(fetchDeployments, pollInterval);
    const sentryInterval = setInterval(fetchSentryTotal, pollInterval);
    const alertInterval = setInterval(fetchCopyTraderAlerts, pollInterval);
    const pwaInterval = enablePwaSwaps ? setInterval(fetchPwaSwaps, pollInterval) : null;

    return () => {
      clearInterval(deploymentInterval);
      clearInterval(sentryInterval);
      clearInterval(alertInterval);
      if (pwaInterval) {
        clearInterval(pwaInterval);
      }
    };
  }, [apiUrl, fetchDeployments, fetchSentryTotal, fetchCopyTraderAlerts, fetchPwaSwaps, pollInterval, enablePwaSwaps]);

  // Don't render if API URL is not configured
  if (!apiUrl) {
    return null;
  }

  return (
    <NotificationContainer $placement={placement}>
      {currentNotification && (
        <NotificationItem
          key={currentNotification.id}
          notification={currentNotification}
          onDismiss={removeNotification}
        />
      )}
    </NotificationContainer>
  );
};

interface NotificationItemProps {
  notification: Notification;
  onDismiss: () => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onDismiss
}) => {
  const getIcon = () => {
    switch (notification.type) {
      case 'token-deployment':
        return '🚀';
      case 'sentry-bought':
        return '💰';
      case 'copy-trader-alert':
        return '📋';
      case 'pwa-swap':
        return '⚡';
      default:
        return '🔔';
    }
  };

  const getTypeClass = () => {
    switch (notification.type) {
      case 'token-deployment':
        return 'notification-deployment';
      case 'sentry-bought':
        return 'notification-sentry';
      case 'copy-trader-alert':
        return 'notification-alert';
      case 'pwa-swap':
        return 'notification-pwa-swap';
      default:
        return '';
    }
  };

  return (
    <NotificationItemWrapper 
      className={`notification-item ${getTypeClass()}`}
    >
      <NotificationContent>
        <NotificationIcon>{getIcon()}</NotificationIcon>
        <NotificationText>
          <NotificationTitle>{notification.title}</NotificationTitle>
          <NotificationMessage>{notification.message}</NotificationMessage>
          <NotificationTime>
            {formatTimeAgo(notification.timestamp)}
          </NotificationTime>
        </NotificationText>
        <NotificationCloseButton onClick={onDismiss}>
          ×
        </NotificationCloseButton>
      </NotificationContent>
    </NotificationItemWrapper>
  );
};

const formatTimeAgo = (date: Date): string => {
  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
  
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
};

// Styled Components
const NotificationContainer = styled.div<{ $placement: 'default' | 'pwaHeader' }>`
  position: fixed;
  top: ${props => props.$placement === 'pwaHeader' ? '76px' : '20px'};
  right: ${props => props.$placement === 'pwaHeader' ? '16px' : '20px'};
  z-index: 10000;
  pointer-events: none;
  max-width: ${props => props.$placement === 'pwaHeader' ? '260px' : '320px'};

  @media (max-width: 640px) {
    top: ${props => props.$placement === 'pwaHeader' ? '68px' : '10px'};
    right: ${props => props.$placement === 'pwaHeader' ? '12px' : '10px'};
    left: ${props => props.$placement === 'pwaHeader' ? 'auto' : '10px'};
    max-width: ${props => props.$placement === 'pwaHeader' ? '220px' : 'calc(100% - 20px)'};
  }
`;

const NotificationItemWrapper = styled.div`
  pointer-events: all;
  background: var(--color-bg-card, #1f2937);
  border-radius: var(--radius-md, 8px);
  box-shadow: var(--shadow-md, 0 4px 6px rgba(0, 0, 0, 0.5));
  overflow: hidden;
  animation: slideInRight 0.3s ease-out;
  transform: translateX(0);
  transition: all 0.3s ease-out;
  border: 1px solid var(--color-border, #333333);

  &.notification-deployment {
    border-left: 3px solid var(--color-accent-green, #10b981);
  }

  &.notification-sentry {
    border-left: 3px solid var(--color-warning, #f59e0b);
  }

  &.notification-alert {
    border-left: 3px solid var(--color-accent-blue, #3b82f6);
  }

  &.notification-pwa-swap {
    border-left: 3px solid var(--color-accent-green, #14F195);
  }
`;

const NotificationContent = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 10px 12px;
  gap: 8px;
`;

const NotificationIcon = styled.div`
  font-size: 18px;
  flex-shrink: 0;
  line-height: 1;
`;

const NotificationText = styled.div`
  flex: 1;
  min-width: 0;
`;

const NotificationTitle = styled.div`
  font-weight: var(--font-weight-semibold, 600);
  font-size: 12px;
  color: var(--color-text-primary, #ffffff);
  margin-bottom: 2px;
  line-height: 1.3;
`;

const NotificationMessage = styled.div`
  font-size: 11px;
  color: var(--color-text-secondary, #a1a1aa);
  line-height: 1.3;
  margin-bottom: 2px;
  word-wrap: break-word;
`;

const NotificationTime = styled.div`
  font-size: 10px;
  color: var(--color-text-muted, #71717a);
`;

const NotificationCloseButton = styled.button`
  background: none;
  border: none;
  font-size: 18px;
  color: var(--color-text-muted, #9ca3af);
  cursor: pointer;
  padding: 0;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: color 0.2s;
  line-height: 1;

  &:hover {
    color: var(--color-text-primary, #ffffff);
  }
`;
